import apiClient from '../../services/apiClient';
import type {
  LearnerRecordDto, StudentBriefDto, NoteDto, NoteKind,
  OutcomeAssessmentEntry, OutcomeLevel, OutcomeListEntry, LearnerStatus,
} from './types';

// Thin wrappers over /api/v1/learner-record (BP 27). The service on the
// other end aggregates every other domain — this file adds no logic of its
// own beyond shaping the HTTP calls.
const BASE = '/api/v1/learner-record';

// ── Student ──────────────────────────────────────────────────────────────

export async function getMyRecord(): Promise<LearnerRecordDto> {
  const { data } = await apiClient.get<LearnerRecordDto>(`${BASE}/me`);
  return data;
}

// ── Instructor ───────────────────────────────────────────────────────────

export interface ListStudentsParams {
  q?: string;
  status?: LearnerStatus;
  limit?: number;
  offset?: number;
}

export async function listStudents(params: ListStudentsParams = {}): Promise<{ students: StudentBriefDto[]; total: number }> {
  const { data } = await apiClient.get<{ students: StudentBriefDto[]; total: number }>(`${BASE}/students`, { params });
  return data;
}

export async function getStudentRecord(userId: string): Promise<LearnerRecordDto> {
  const { data } = await apiClient.get<LearnerRecordDto>(`${BASE}/students/${userId}`);
  return data;
}

export async function addNote(userId: string, body: {
  kind: NoteKind; body: string; related_type?: string; related_id?: string; supersedes_id?: string;
}): Promise<NoteDto> {
  const { data } = await apiClient.post<{ note: NoteDto }>(`${BASE}/students/${userId}/notes`, body);
  return data.note;
}

export async function addOutcomeAssessment(userId: string, body: {
  outcome_code: string; level: OutcomeLevel; evidence: string; supersedes_id?: string;
}): Promise<OutcomeAssessmentEntry & { outcome_code: string; id: string }> {
  const { data } = await apiClient.post<{ assessment: OutcomeAssessmentEntry & { outcome_code: string; id: string } }>(
    `${BASE}/students/${userId}/outcomes`, body,
  );
  return data.assessment;
}

export async function setLearnerStatus(userId: string, body: { learner_status: LearnerStatus; reason: string }): Promise<{
  id: string; student_id: string | null; name: string | null; learner_status: LearnerStatus; learner_status_changed_at: string | null;
}> {
  const { data } = await apiClient.put(`${BASE}/students/${userId}/status`, body);
  return data.student;
}

export async function listOutcomes(): Promise<OutcomeListEntry[]> {
  const { data } = await apiClient.get<{ outcomes: OutcomeListEntry[] }>(`${BASE}/outcomes`);
  return data.outcomes ?? [];
}

export interface SeedOutcomesResult { created: number; updated: number }

export async function seedOutcomes(): Promise<SeedOutcomesResult> {
  const { data } = await apiClient.post<SeedOutcomesResult>(`${BASE}/outcomes/seed`);
  return data;
}

export function studentExportCsvPath(userId: string): string {
  return `${BASE}/students/${userId}/export.csv`;
}

export function cohortExportCsvPath(status?: LearnerStatus): string {
  return status ? `${BASE}/cohort/export.csv?status=${status}` : `${BASE}/cohort/export.csv`;
}
